import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import {
  ShieldCheck, Video, GraduationCap, Award, Library, GitMerge, Brain, Building2,
  CheckCircle2, ArrowRight, Radar, Truck, Users, BarChart3
} from "lucide-react";

const FEATURES = [
  { icon: Video, title: "Cursos em vídeo com anti-abandono", desc: "Player com checkpoints, verificações de presença aleatórias e registro de tentativas. Conteúdo que não pode ser ignorado." },
  { icon: GitMerge, title: "Trilhas por cargo", desc: "Cada cargo (Operador, Pronta Resposta, Escolta…) com sua trilha obrigatória. Progresso e horas calculados automaticamente." },
  { icon: Award, title: "Certificação automática", desc: "Certificado em PDF com QR Code de validação, código único e assinatura digital — gerado no momento da aprovação." },
  { icon: Library, title: "Biblioteca corporativa", desc: "POPs, fluxogramas, políticas e manuais com controle de versão e registro de ciência (IP, data, versão)." },
  { icon: Brain, title: "IA gera o curso para você", desc: "Faça upload de um PDF, DOCX ou PPTX e a IA estrutura módulos, resumo e avaliações em segundos." },
  { icon: BarChart3, title: "Dashboard executivo", desc: "RH, operacional e compliance num só lugar: horas treinadas, certificações, reciclagens vencidas e auditoria." },
];

const TARGETS = ["Monitoramento", "Rastreamento", "Pronta Resposta", "Escolta", "Segurança Patrimonial", "Logística", "Gerenciamento de Riscos"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-[var(--unigr-text)]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-[var(--unigr-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#features" className="hover:text-[var(--unigr-navy)]">Plataforma</a>
            <a href="#segments" className="hover:text-[var(--unigr-navy)]">Aplicação</a>
            <a href="#how" className="hover:text-[var(--unigr-navy)]">Como funciona</a>
            <a href="#contact" className="hover:text-[var(--unigr-navy)]">Contato</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="landing-login-btn">Entrar</Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="btn-navy" data-testid="landing-cta-btn">Começar agora</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="eyebrow mb-4">Capacitação Operacional · Compliance · Certificação</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              A plataforma de treinamento das empresas de
              <span className="text-[var(--unigr-red)]"> segurança</span> e
              <span className="text-[var(--unigr-navy)]"> gerenciamento de riscos</span>.
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 max-w-2xl">
              UNIGR padroniza operações, reduz erros e garante conformidade em empresas de monitoramento,
              rastreamento, pronta resposta, escolta e logística. Treine, certifique e audite seus colaboradores
              de ponta a ponta — com vídeos interativos, trilhas por cargo e certificação automatizada.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button size="lg" className="btn-navy w-full sm:w-auto" data-testid="hero-cta-primary">
                  Solicitar demonstração
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="hero-cta-secondary">
                  Entrar na plataforma
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-2">
              {TARGETS.map((t) => (
                <span key={t} className="text-xs font-medium px-3 py-1.5 rounded-full border border-[var(--unigr-border)] bg-white text-slate-600">
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 fade-up delay-150">
            <div className="rounded-2xl border border-[var(--unigr-border)] shadow-sm overflow-hidden bg-white">
              <img
                src="https://images.pexels.com/photos/30692441/pexels-photo-30692441.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Sala de monitoramento"
                className="w-full h-64 object-cover"
              />
              <div className="p-6 border-t border-[var(--unigr-border)]">
                <div className="flex items-center gap-3 mb-3">
                  <ShieldCheck className="w-5 h-5 text-[var(--unigr-navy)]" />
                  <div className="font-display font-bold">Operação certificada, auditoria pronta</div>
                </div>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />Certificados com QR Code de validação</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />Reciclagem automática (6/12/24 meses)</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />Registro de ciência (IP, data, versão)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-[var(--unigr-muted)] border-y border-[var(--unigr-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl">
            <div className="eyebrow mb-3">Plataforma</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              Mais que um EAD — uma engrenagem operacional.
            </h2>
            <p className="mt-4 text-slate-600">
              Construída para o cotidiano de centrais de monitoramento, gerenciadoras de risco e operações logísticas.
              Cada recurso existe para padronizar conduta, registrar ciência e produzir evidência para auditoria.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="bg-white border border-[var(--unigr-border)] rounded-xl p-6 hover:shadow-md transition-shadow fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[var(--unigr-navy)] text-white mb-4">
                  <f.icon className="w-5 h-5" />
                </div>
                <div className="font-display font-bold text-lg">{f.title}</div>
                <div className="mt-2 text-sm text-slate-600">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Segments */}
      <section id="segments" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="https://images.pexels.com/photos/2800121/pexels-photo-2800121.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Frota logística"
              className="rounded-xl border border-[var(--unigr-border)] w-full object-cover h-80"
            />
          </div>
          <div>
            <div className="eyebrow mb-3">Aplicação</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              Feita para a realidade de quem opera 24/7.
            </h2>
            <p className="mt-4 text-slate-600">
              Capacitamos do operador de monitoramento ao coordenador de pronta resposta —
              passando por escolta, rastreamento, analista de riscos e equipes administrativas.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {[
                ["Operador de Monitoramento", Radar],
                ["Agente de Pronta Resposta", ShieldCheck],
                ["Escolta", Truck],
                ["Analista de Riscos", GitMerge],
                ["Supervisor Operacional", Users],
                ["Coordenador Operacional", Building2],
              ].map(([n, I]) => (
                <div key={n} className="flex items-center gap-3 bg-white border border-[var(--unigr-border)] rounded-md px-4 py-3">
                  <I className="w-4 h-4 text-[var(--unigr-red)]" />
                  <span className="text-sm">{n}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How */}
      <section id="how" className="bg-[var(--unigr-navy)] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-3 gap-10">
            <div>
              <div className="eyebrow text-white/70 mb-3">Como funciona</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                Do upload do POP ao certificado validado por QR — em horas, não meses.
              </h2>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-3 gap-6">
              {[
                ["01", "Estruture", "Cadastre filiais, departamentos, cargos e colaboradores. Defina trilhas obrigatórias por cargo."],
                ["02", "Capacite", "Suba vídeos ou agende treinamentos ao vivo. A IA gera cursos a partir dos seus POPs."],
                ["03", "Certifique", "Avaliações automáticas, certificados com QR Code e reciclagem agendada por validade."],
              ].map(([n, t, d]) => (
                <div key={n} className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <div className="font-mono text-3xl text-[var(--unigr-red)] font-bold">{n}</div>
                  <div className="font-display font-bold text-xl mt-2">{t}</div>
                  <div className="mt-2 text-sm text-white/70">{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="eyebrow mb-3">Comece hoje</div>
        <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight">
          Padronize a operação. Certifique sua equipe.
        </h2>
        <p className="mt-4 max-w-2xl mx-auto text-slate-600">
          Teste com o ambiente demo, conheça o backoffice e leve a UNIGR para sua central.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/register">
            <Button size="lg" className="btn-navy" data-testid="footer-cta-primary">Solicitar acesso</Button>
          </Link>
          <Link to="/login">
            <Button size="lg" variant="outline" data-testid="footer-cta-secondary">Acessar plataforma</Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-[var(--unigr-border)] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size={28} />
          <div className="text-xs text-slate-500">© 2026 UNIGR · conhecimento que transforma</div>
        </div>
      </footer>
    </div>
  );
}
